import { useEffect, useState } from "react";
import { initialState, DataDisplayContext, DataDisplayState, DATA_DISPLAY_CHANGE_TYPE, DataDisplayFormDetails, DataDisplayAgent, DataDisplayClient, DataDisplayFormDetail, DataDisplayProduct, DataDisplayServiceAgent, DataDisplayServiceClient } from "./data-display-context";
import type { FC, ReactNode } from "react";
import { useGetDisplayPreferenceTemplate } from "~/queries/useGetDisplayPreferenceTemplate";
import { useMe } from "~/queries/useMe";
import { useCreateOrUpdateDisplayPreference } from "~/queries/useCreateOrUpdateDisplayPreference";
import { DISPLAY_PREFERENCE_TYPE } from "~/types/__generated/gql/graphql";
import toast from "react-hot-toast";
interface DataDisplayProviderProps {
  children?: ReactNode;
}
export const DataDisplayProvider: FC<DataDisplayProviderProps> = props => {
  const {
    children
  } = props;
  const [state, setState] = useState<DataDisplayState>(initialState);
  const createOrUpdateDisplayPreference = useCreateOrUpdateDisplayPreference();
  const {
    data: template
  } = useGetDisplayPreferenceTemplate();
  const {
    data: me
  } = useMe();
  useEffect(() => {
    if (me?._displayPreference) {
      if (template) {
        const agent = combineDisplayPreference({
          ...initialState.agent,
          ...(state.agent.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.agent as unknown as DataDisplayAgent)
        }, {
          ...initialState.agent,
          ...(state.agent.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(template.agent as unknown as DataDisplayClient)
        });
        const client = combineDisplayPreference({
          ...initialState.client,
          ...(state.client.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.client as unknown as DataDisplayAgent)
        }, {
          ...initialState.client,
          ...(state.client.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(template.client as unknown as DataDisplayClient)
        });
        const product = {
          ...initialState.product,
          ...(state.product.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.product as unknown as DataDisplayProduct)
        };
        const serviceAgent = {
          ...initialState.serviceAgent,
          ...(state.serviceAgent.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.serviceAgent as unknown as DataDisplayServiceAgent)
        };
        const serviceClient = {
          ...initialState.serviceClient,
          ...(state.serviceClient.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.serviceClient as unknown as DataDisplayServiceClient)
        };
        return setState({
          agent,
          client,
          product,
          serviceAgent,
          serviceClient
        });
      } else {
        return setState({
          agent: {
            ...initialState.agent,
            ...(state.agent.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.agent as unknown as DataDisplayAgent)
          },
          client: {
            ...initialState.client,
            ...(state.client.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.client as unknown as DataDisplayClient)
          },
          product: {
            ...initialState.product,
            ...(state.product.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.product as unknown as DataDisplayProduct)
          },
          serviceAgent: {
            ...initialState.serviceAgent,
            ...(state.serviceAgent.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.serviceAgent as unknown as DataDisplayServiceAgent)
          },
          serviceClient: {
            ...initialState.serviceClient,
            ...(state.serviceClient.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.serviceClient as unknown as DataDisplayServiceClient)
          }
        });
      }
    }
    if (template) {
      return setState({
        agent: {
          ...initialState.agent,
          ...(template.agent as unknown as DataDisplayAgent)
        },
        client: {
          ...initialState.client,
          ...(template.client as unknown as DataDisplayClient)
        },
        product: {
          ...initialState.product,
          ...(template.product as unknown as DataDisplayProduct)
        },
        serviceAgent: {
          ...initialState.serviceAgent,
          ...(template.serviceAgent as unknown as DataDisplayServiceAgent)
        },
        serviceClient: {
          ...initialState.serviceClient,
          ...(template.serviceClient as unknown as DataDisplayServiceClient)
        }
        // if no template and no user preference, will use the initial state which is already set
      });
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, template]);
  const handleAgentFormModelOpen = () => {
    const newState = {
      ...state,
      agent: {
        ...state.agent,
        isFormModelOpen: true
      }
    };
    setState(newState);
  };
  const handleAgentFormModelClose = () => {
    const newState = {
      ...state,
      agent: {
        ...state.agent,
        isFormModelOpen: false
      }
    };
    setState(newState);
  };
  const handleAgentListModelOpen = () => {
    const newState = {
      ...state,
      agent: {
        ...state.agent,
        isListModelOpen: true
      }
    };
    setState(newState);
  };
  const handleAgentListModelClose = () => {
    const newState = {
      ...state,
      agent: {
        ...state.agent,
        isListModelOpen: false
      }
    };
    setState(newState);
  };
  const handleChangeAgentListSettings = async (change: any[]) => {
    const newState = {
      ...state,
      agent: {
        ...state.agent,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleChangeAgentFormSettings = async (type: DATA_DISPLAY_CHANGE_TYPE, change: any[]) => {
    const newState = {
      ...state,
      agent: {
        ...state.agent,
        form: {
          ...state.agent.form,
          [`${type}`]: change
        }
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleClientFormModelOpen = () => {
    const newState = {
      ...state,
      client: {
        ...state.client,
        isFormModelOpen: true
      }
    };
    setState(newState);
  };
  const handleClientFormModelClose = () => {
    const newState = {
      ...state,
      client: {
        ...state.client,
        isFormModelOpen: false
      }
    };
    setState(newState);
  };
  const handleClientListModelOpen = () => {
    const newState = {
      ...state,
      client: {
        ...state.client,
        isListModelOpen: true
      }
    };
    setState(newState);
  };
  const handleClientListModelClose = () => {
    const newState = {
      ...state,
      client: {
        ...state.client,
        isListModelOpen: false
      }
    };
    setState(newState);
  };
  const handleChangeClientListSettings = async (change: any[]) => {
    const newState = {
      ...state,
      client: {
        ...state.client,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleChangeClientFormSettings = async (type: DATA_DISPLAY_CHANGE_TYPE, change: any[]) => {
    const newState = {
      ...state,
      client: {
        ...state.client,
        form: {
          ...state.client.form,
          [`${type}`]: change
        }
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleProductListModelOpen = () => {
    const newState = {
      ...state,
      product: {
        ...state.product,
        isListModelOpen: true
      }
    };
    setState(newState);
  };
  const handleProductListModelClose = () => {
    const newState = {
      ...state,
      product: {
        ...state.product,
        isListModelOpen: false
      }
    };
    setState(newState);
  };
  const handleChangeProductListSettings = async (change: any[]) => {
    const newState = {
      ...state,
      product: {
        ...state.product,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleChangeServiceAgentListSettings = async (change: any[]) => {
    const newState = {
      ...state,
      serviceAgent: {
        ...state.serviceAgent,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleChangeServiceClientListSettings = async (change: any[]) => {
    const newState = {
      ...state,
      serviceClient: {
        ...state.serviceClient,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleUpdateDisplayPreference = async (state: DataDisplayState) => {
    await toast.promise(
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
      try {
        await createOrUpdateDisplayPreference.mutateAsync({
          input: {
            agent: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.AGENT,
              list: state.agent.list,
              form: state.agent.form
            },
            client: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.CLIENT,
              list: state.client.list,
              form: state.client.form
            },
            product: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.PRODUCT,
              list: state.product.list
            },
            serviceAgent: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.SERVICE_AGENT,
              list: state.serviceAgent.list
            },
            serviceClient: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.SERVICE_CLIENT,
              list: state.serviceClient.list
            }
            // serviceAgent and serviceClient are using agent and client list/form because they are the same for now, will be different in the future when we have service form
          }
        });
        resolve(null);
      } catch (e) {
        reject(e instanceof Error ? e : new Error("Something went wrong"));
      }
    }), {
      loading: "Updating display preference...",
      success: "Display preference update successfully",
      error: "Failed to upload display preference"
    });
  };
  const combineDisplayPreference = (user: DataDisplayAgent | DataDisplayClient, template: DataDisplayAgent | DataDisplayClient) => {
    // handle info section
    const userInfo = user.form.info.flatMap(i => i.fields);
    const templateInfo = template.form.info.flatMap(i => i.fields);
    const extraInfo = templateInfo.filter(i => !userInfo.find(u => u.fieldName === i.fieldName));
    let combinedInfo = user.form.info;
    if (userInfo.length !== templateInfo.length) {
      combinedInfo = getCustomFieldForm(extraInfo, user.form.info);
    }

    // handle address section
    const userAddr = user.form.addresses.flatMap(i => i.fields);
    const templateAddr = template.form.addresses.flatMap(i => i.fields);
    const extraAddr = templateInfo.filter(i => !userInfo.find(u => u.fieldName === i.fieldName));
    let combinedAddr = user.form.addresses;
    if (userAddr.length !== templateAddr.length) {
      combinedAddr = getCustomFieldForm(extraAddr, user.form.addresses, "addresses");
    }

    // handle contact section
    const userCont = user.form.contacts.flatMap(i => i.fields);
    const templateCont = template.form.contacts.flatMap(i => i.fields);
    const extraCont = templateInfo.filter(i => !userInfo.find(u => u.fieldName === i.fieldName));
    let combinedCont = user.form.contacts;
    if (userCont.length !== templateCont.length) {
      combinedCont = getCustomFieldForm(extraCont, user.form.contacts, "contacts");
    }
    return {
      ...template,
      list: user.list ?? [],
      form: {
        info: combinedInfo,
        addresses: combinedAddr,
        contacts: combinedCont,
        service: initialState.agent.form.service
      }
    } as unknown as DataDisplayAgent;
  };
  const getCustomFieldForm = (extra: DataDisplayFormDetail[], form: DataDisplayFormDetails[], parentFieldName?: string) => {
    let returnArr = form;
    let rowIndex = form.length;
    let formIndex = form.map(f => f.fields).flat().length;
    for (let i = 0; i < extra.length; i += 4) {
      const customFields = {
        sortingIndex: rowIndex,
        fields: extra.slice(i, i + 4).map(c => {
          return {
            parentFieldName,
            fieldName: c.fieldName,
            fieldLabel: c.fieldLabel,
            fieldType: c.fieldType,
            sortingIndex: formIndex++,
            isCustomField: true,
            isPinned: c.isPinned
          };
        })
      };
      returnArr = [...returnArr, customFields];
      rowIndex++;
    }
    return returnArr;
  };
  return <DataDisplayContext.Provider value={{
    ...state,
    handleAgentFormModelOpen,
    handleAgentFormModelClose,
    handleAgentListModelOpen,
    handleAgentListModelClose,
    handleChangeAgentListSettings,
    handleChangeAgentFormSettings,
    handleClientFormModelOpen,
    handleClientFormModelClose,
    handleClientListModelOpen,
    handleClientListModelClose,
    handleChangeClientListSettings,
    handleChangeClientFormSettings,
    handleProductListModelOpen,
    handleProductListModelClose,
    handleChangeProductListSettings,
    handleChangeServiceAgentListSettings,
    handleChangeServiceClientListSettings
  }} data-sentry-element="unknown" data-sentry-component="DataDisplayProvider" data-sentry-source-file="data-display-provider.tsx">
			{children}
		</DataDisplayContext.Provider>;
};